import { IncubatorModels } from '@eppendorf/app-configs';
import { DeviceShadowDTO } from '@eppendorf/vnls-device-service-utils';

const INFINITE_DURATION_SENTINEL = 'PT9223372036854775807.807S';

/** Shown instead of the timer when it is switched off, same as on the device */
const COUNTDOWN_TIMER_OFF_PLACEHOLDER = '--:--';

/** Telemetry IDs of timers shown as counting down on the device, per device model */
const COUNTDOWN_TIMER_PARAMETERS = new Map<string, Set<string>>([
  [IncubatorModels.CELL_XPERT_CS220, new Set(['AgitationTimer'])],
]);

/**
 * Check if a parameter is a countdown timer (CS220 Agitation Timer)
 */
export function isCountdownTimerParameter(
  device?: { model?: string } | null,
  telemetryId?: string,
): boolean {
  if (!device?.model || !telemetryId) return false;
  return !!COUNTDOWN_TIMER_PARAMETERS.get(device.model)?.has(telemetryId);
}

/**
 * Find the shadow component of a countdown timer.
 * The setpoint is sent on "<telemetryId>Function" (e.g. "AgitationTimerFunction").
 */
export function findCountdownTimerShadowComp(
  components: DeviceShadowDTO['components'],
  telemetryId: string,
) {
  return (
    components?.find((item) => item.id === telemetryId) ??
    components?.find((item) => item.id === `${telemetryId}Function`)
  );
}

/**
 * Parse a duration ("PT270S" or raw seconds) into seconds
 */
export function parseDurationSeconds(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  if (value === INFINITE_DURATION_SENTINEL) return undefined;

  const strValue = String(value);
  const isoSeconds = strValue.match(/^PT([\d.]+)S$/)?.[1];
  const seconds = Number.parseFloat(isoSeconds ?? strValue);

  return Number.isFinite(seconds) ? seconds : undefined;
}

/**
 * Get remaining seconds of a countdown timer (setpoint - elapsed).
 * Returns undefined if there is no setpoint.
 */
export function getRemainingSeconds(
  elapsed: unknown,
  setPoint: unknown,
): number | undefined {
  const total = parseDurationSeconds(setPoint);
  if (total === undefined) return undefined;

  const elapsedSeconds = parseDurationSeconds(elapsed) ?? 0;
  return Math.max(Math.round(total - elapsedSeconds), 0);
}

/**
 * Get the value and setpoint of a countdown timer (CS220 Agitation Timer).
 * Shows "--:--" when the timer is off, otherwise the remaining time.
 */
export function getCountdownTimerDisplay<
  T extends { state?: string; setPoint?: unknown },
>(
  elapsed: string | undefined,
  shadowComponent?: T,
): { value?: string; setPoint?: T['setPoint'] } {
  if (shadowComponent?.state === 'Off') {
    return { value: COUNTDOWN_TIMER_OFF_PLACEHOLDER };
  }

  const remainingSeconds = getRemainingSeconds(elapsed, shadowComponent?.setPoint);
  return {
    value: remainingSeconds === undefined ? elapsed : `PT${remainingSeconds}S`,
    setPoint: shadowComponent?.setPoint,
  };
}
